import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  Divider,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import CollapsibleSection from '../../../shared/components/CollapsibleSection';
import InlineAlert from '../../../shared/components/InlineAlert';
import FormActionBar from '../../../shared/components/FormActionBar';
import AWSConsoleButton from '../../../shared/components/AWSConsoleButton';
import AWSCliCredentials from '../../../shared/components/AWSCliCredentials';
import { AWS_REGIONS } from '../../../shared/constants/aws';
import RoleTestResultPanel from './RoleTestResultPanel';
import { useAccountForm, getAccountIdError, getRoleNameError } from '../hooks/useAccountForm';

interface AccountFormFieldsProps {
  form: ReturnType<typeof useAccountForm>;
  allowWriteConsole?: boolean;
  /** Both switches below save through admin-level actions, so only an admin
      is shown them: a control that moves and cannot be saved is not a control. */
  allowAccountDecisions?: boolean;
  /** The automation and onboarding role fields name the administrator roles
      Cloud2 assumes in the customer's account, and the server lets only an
      admin set or change them. Below admin both are shown, disabled. */
  allowAdminRoles?: boolean;
  /** Whether account_baseline is on. Off, every baseline action is refused, so
      neither decision is shown to anyone. */
  baselineEnabled: boolean;
  handleDeleteClick: (() => void) | undefined;
}

const AccountFormFields: React.FC<AccountFormFieldsProps> = ({
  form,
  allowWriteConsole = false,
  allowAccountDecisions = false,
  allowAdminRoles = false,
  baselineEnabled,
  handleDeleteClick,
}) => {
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const adminOnlyHint = allowAdminRoles ? undefined : 'Only an admin may change this';

  const showDecisions = baselineEnabled && allowAccountDecisions;

  // The backend validates the test region against the account's persisted
  // supported_regions, so the buttons send the saved region, not the edited form value.
  const savedRegion = form.selectedEntity?.main_region;

  // When account_baseline could not be read, the two decisions are unknown:
  // their switches stay disabled and the save leaves them out. A new account
  // has nothing to read, so this only applies to an existing one.
  const decisionsError = form.isNew ? undefined : form.selectedEntity?.decisions_error;

  const accountIdError = useMemo(
    () => getAccountIdError(form.formData.aws_account_id, form.entities, form.selectedEntity?.id),
    [form.formData.aws_account_id, form.entities, form.selectedEntity?.id],
  );
  const readRoleError = useMemo(
    () => getRoleNameError(form.formData.read_role_name),
    [form.formData.read_role_name],
  );
  const writeRoleError = useMemo(
    () => form.formData.write_role_name ? getRoleNameError(form.formData.write_role_name) : null,
    [form.formData.write_role_name],
  );
  const automationRoleError = useMemo(
    () => form.formData.automation_role_name ? getRoleNameError(form.formData.automation_role_name) : null,
    [form.formData.automation_role_name],
  );
  const onboardingRoleError = useMemo(
    () => form.formData.onboarding_role_name ? getRoleNameError(form.formData.onboarding_role_name) : null,
    [form.formData.onboarding_role_name],
  );

  return (
    // No scroll of its own: the modal's detail pane scrolls this together
    // with the onboarding panel above it.
    <Box sx={{ flexShrink: 0, p: 3 }}>
      <Box sx={{ maxWidth: 800 }}>
        <Box
          sx={{
            display: 'flex',
            // On a phone the heading and the access buttons cannot share a
            // line: the heading breaks mid-word and the buttons stack into a
            // column beside it. Wrapping puts them on their own row instead.
            flexWrap: 'wrap',
            rowGap: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography variant="h6">
            {form.isNew ? 'New AWS Account' : 'Account Details'}
          </Typography>
          {!form.isNew && form.selectedEntity && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              <AWSConsoleButton
                customerId={form.customerId}
                accountId={form.selectedEntity.aws_account_id}
                region={form.selectedEntity.main_region}
                allowWrite={allowWriteConsole}
              />
              <Divider orientation="vertical" flexItem />
              <AWSCliCredentials
                customerId={form.customerId}
                accountId={form.selectedEntity.aws_account_id}
                allowWrite={allowWriteConsole}
              />
            </Box>
          )}
        </Box>
        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'grid', gap: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label="AWS Account ID *"
              value={form.formData.aws_account_id}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                form.handleChange('aws_account_id', value);
              }}
              placeholder="123456789012"
              inputProps={{
                maxLength: 12,
                pattern: '[0-9]{12}',
                inputMode: 'numeric'
              }}
              error={!!accountIdError}
              helperText={accountIdError || "12-digit AWS account number"}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Account Name *"
              value={form.formData.account_name}
              onChange={(e) => form.handleChange('account_name', e.target.value)}
              placeholder="Production"
              helperText="Friendly name for this account"
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <TextField
            fullWidth
            label="Main Region"
            value={form.formData.main_region ? (AWS_REGIONS.find(r => r.value === form.formData.main_region)?.label || form.formData.main_region) : 'Not configured'}
            disabled
            InputLabelProps={{ shrink: true }}
            helperText={!form.formData.main_region ? "Configure in Account Details below" : undefined}
          />

          {/*
            Only on an existing account: the create request has no `enabled`
            field, and a disabled account is skipped by everything, so nothing
            will onboard it until this is on.
          */}
          {!form.isNew && (
            <FormControlLabel
              control={
                <Switch
                  checked={form.formData.enabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.handleChange('enabled', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Active</Typography>
                  <Typography variant="caption" color="text.secondary">
                    A disabled account is skipped, including by onboarding
                  </Typography>
                </Box>
              }
            />
          )}

          {/*
            Shown when creating too, unlike the switch below: it decides how the
            access roles are built, and they are installed once. Changing it
            later means reconverging the roles stack, not flipping a flag.

            Admins only, because saving it goes through
            account_baseline.SetAccountBreakGlass, which is admin level; and
            only while account_baseline is on, since every baseline action is
            refused while it is off.
          */}
          {showDecisions && decisionsError && (
            <InlineAlert severity="error">
              {decisionsError}. The two switches below are disabled until they load,
              and saving leaves them as they are.
            </InlineAlert>
          )}

          {showDecisions && (
            <FormControlLabel
              control={
                <Switch
                  disabled={!!decisionsError}
                  checked={form.formData.break_glass_required ?? false}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.handleChange('break_glass_required', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Customer controls our admin access</Typography>
                  <Typography variant="caption" color="text.secondary">
                    We stay read-only here. The customer runs break-glass in their own tenant to
                    let us in, and closes it afterwards. For customers who may not grant us
                    standing admin.
                  </Typography>
                </Box>
              }
            />
          )}

          {/*
            Beside Active, because the two are easy to confuse and the
            difference matters: Active says OpsPilot manages the account at
            all, this says OpsPilot installs and keeps its baseline stacks.
            Off by default, and moved one account at a time during the
            migration off Service Catalog.
          */}
          {!form.isNew && showDecisions && (
            <FormControlLabel
              control={
                <Switch
                  disabled={!!decisionsError}
                  checked={form.formData.baseline_managed ?? false}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.handleChange('baseline_managed', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">OpsPilot installs the baselines</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Turn on to move this account onto direct CloudFormation. Until then its
                    baselines stay with Service Catalog. Admins only: it starts changing
                    this account within the hour.
                  </Typography>
                </Box>
              }
            />
          )}
        </Box>

        <Box sx={{ mt: 3 }}>
          <CollapsibleSection
            title="Account Details"
            expanded={detailsExpanded}
            onChange={setDetailsExpanded}
          >
            <Box sx={{ display: 'grid', gap: 3 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Read-Only Role Name *"
                  value={form.formData.read_role_name}
                  onChange={(e) => form.handleChange('read_role_name', e.target.value)}
                  placeholder="cloud2-operator-readonly"
                  error={readRoleError !== null}
                  helperText={readRoleError || "IAM role name for read-only access (investigations)"}
                  InputLabelProps={{ shrink: true }}
                />
                {!form.isNew && form.selectedEntity && (
                  <Button
                    variant="contained"
                    startIcon={form.testing ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                    onClick={() => form.handleTestRole('readonly', savedRegion)}
                    disabled={form.testing || !form.formData.read_role_name?.trim() || readRoleError !== null}
                    sx={{ mt: 1, whiteSpace: 'nowrap' }}
                  >
                    Test
                  </Button>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Write Role Name"
                  value={form.formData.write_role_name}
                  onChange={(e) => form.handleChange('write_role_name', e.target.value)}
                  placeholder="cloud2-operator-admin"
                  error={writeRoleError !== null}
                  helperText={writeRoleError || "IAM role name for write access (remediation) - optional"}
                  InputLabelProps={{ shrink: true }}
                />
                {!form.isNew && form.selectedEntity && (
                  <Button
                    variant="contained"
                    startIcon={form.testing ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                    onClick={() => form.handleTestRole('write', savedRegion)}
                    disabled={form.testing || !form.formData.write_role_name?.trim() || writeRoleError !== null}
                    sx={{ mt: 1, whiteSpace: 'nowrap' }}
                  >
                    Test
                  </Button>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Automation Role Name"
                  value={form.formData.automation_role_name}
                  onChange={(e) => form.handleChange('automation_role_name', e.target.value)}
                  placeholder="cloud2-operator-automation"
                  disabled={!allowAdminRoles}
                  error={automationRoleError !== null}
                  helperText={
                    automationRoleError
                    || adminOnlyHint
                    || "IAM role for automation (Lambda invoke, SSM, Service Catalog) - optional"
                  }
                  InputLabelProps={{ shrink: true }}
                />
                {!form.isNew && form.selectedEntity && (
                  <Button
                    variant="contained"
                    startIcon={form.testing ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                    onClick={() => form.handleTestRole('automation', savedRegion)}
                    disabled={form.testing || !form.formData.automation_role_name?.trim() || automationRoleError !== null}
                    sx={{ mt: 1, whiteSpace: 'nowrap' }}
                  >
                    Test
                  </Button>
                )}
              </Box>

              {!form.isNew && form.selectedEntity && form.testResult && (
                <RoleTestResultPanel testResult={form.testResult} />
              )}

              {/*
                The one role in this group the customer installs. The other
                three name roles OpsPilot creates, and they are empty on a
                fresh account because they do not exist yet; this one is what
                lets OpsPilot in to create them.
              */}
              <TextField
                fullWidth
                label="Onboarding Role Name"
                value={form.formData.onboarding_role_name}
                onChange={(e) => form.handleChange('onboarding_role_name', e.target.value)}
                placeholder="cloud2-onboarding"
                disabled={!allowAdminRoles}
                error={onboardingRoleError !== null}
                helperText={
                  onboardingRoleError
                  || adminOnlyHint
                  || 'Installed by the customer before onboarding starts. OpsPilot assumes it to create the other roles.'
                }
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="External ID"
                value={form.formData.external_id}
                onChange={(e) => form.handleChange('external_id', e.target.value)}
                placeholder="optional-external-id"
                helperText="Optional external ID for security"
                InputLabelProps={{ shrink: true }}
              />

              <FormControl fullWidth>
                <InputLabel shrink>Supported Regions *</InputLabel>
                <Select
                  multiple
                  value={form.formData.supported_regions}
                  onChange={(e) => {
                    const value = e.target.value as string[];
                    form.setError(null);
                    const updated = {
                      supported_regions: value,
                      main_region: !value.includes(form.formData.main_region)
                        ? (value.length > 0 ? value[0] : '')
                        : form.formData.main_region,
                    };
                    form.handleChange('supported_regions', updated.supported_regions);
                    if (updated.main_region !== form.formData.main_region) {
                      form.handleChange('main_region', updated.main_region);
                    }
                  }}
                  input={<OutlinedInput label="Supported Regions *" notched />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={AWS_REGIONS.find(r => r.value === value)?.value || value}
                          size="small"
                          color={value === form.formData.main_region ? 'primary' : 'default'}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {AWS_REGIONS.map((region) => (
                    <MenuItem key={region.value} value={region.value}>
                      {region.label} {region.value === form.formData.main_region ? '(Main)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel shrink>Main Region *</InputLabel>
                <Select
                  value={form.formData.main_region}
                  onChange={(e) => {
                    form.handleChange('main_region', e.target.value);
                  }}
                  label="Main Region *"
                  notched
                  disabled={form.formData.supported_regions.length === 0}
                >
                  {form.formData.supported_regions.length === 0 ? (
                    <MenuItem value="">
                      <em>Select supported regions first</em>
                    </MenuItem>
                  ) : (
                    form.formData.supported_regions.map((regionValue) => {
                      const region = AWS_REGIONS.find(r => r.value === regionValue);
                      return (
                        <MenuItem key={regionValue} value={regionValue}>
                          {region?.label || regionValue}
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
                {form.formData.supported_regions.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                    Select one or more supported regions first
                  </Typography>
                )}
              </FormControl>

              <TextField
                fullWidth
                label="Event Bus Name"
                value={form.formData.event_bus_name}
                onChange={(e) => form.handleChange('event_bus_name', e.target.value)}
                placeholder="cloud2-events"
                helperText="EventBridge bus name for Service Catalog automation events"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </CollapsibleSection>
        </Box>

        <FormActionBar
          onSave={form.handleSave}
          saving={form.saving}
          saveDisabled={form.saving || !form.hasChanges()}
          saveLabel={form.isNew ? 'Create Account' : 'Save Changes'}
          savingLabel={form.isNew ? 'Creating...' : 'Saving...'}
          onDelete={!form.isNew && form.selectedEntity ? handleDeleteClick : undefined}
          deleteLabel="Delete Account"
        />
      </Box>
    </Box>
  );
};

export default AccountFormFields;
